package org.teddapp.project.core

import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.query.Order
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.long
import kotlinx.serialization.json.put

/** Table du journal global : une ligne par fait, jamais modifiée après écriture. */
private const val JOURNAL_TABLE = "tedd_event"

/**
 * Transport du journal vers Supabase. Il ne décide de rien : il publie ce que le
 * noyau lui donne et rend ce que le broker a accepté.
 *
 * L'idempotence est portée par la clé primaire `id`, qui est l'identifiant du fait.
 * Une enveloppe publiée deux fois n'écrase rien : `ignoreDuplicates` laisse la
 * première en place, et l'ordre de rejeu vient de `global_offset`, attribué par
 * Postgres, jamais de l'horodatage de l'appareil.
 */
class SupabaseJournalTransport(
    private val client: SupabaseClient,
    private val userId: String,
) : CoreJournalTransport {

    override suspend fun publish(envelopes: List<String>): List<String> {
        if (envelopes.isEmpty()) return emptyList()
        val rows = envelopes.mapNotNull { envelope ->
            val id = jsonField(envelope, "event_id") ?: return@mapNotNull null
            buildJsonObject {
                put("id", id)
                put("device_id", jsonField(envelope, "device_id") ?: "inconnu")
                put("user_id", jsonField(envelope, "user_id") ?: userId)
                put("timestamp_ms", jsonField(envelope, "timestamp_ms")?.toLongOrNull() ?: 0L)
                put("envelope", envelope)
            }
        }
        if (rows.isEmpty()) return emptyList()
        val accepted: List<JsonObject> = client.from(JOURNAL_TABLE)
            .upsert(rows) {
                onConflict = "id"
                ignoreDuplicates = true
                select()
            }
            .decodeList()
        return accepted.mapNotNull(::toGlobalRecord)
    }

    override suspend fun fetchAfter(after: Long): List<String> {
        val rows: List<JsonObject> = client.from(JOURNAL_TABLE)
            .select {
                filter {
                    eq("user_id", userId)
                    gt("global_offset", after)
                }
                order(column = "global_offset", order = Order.ASCENDING)
            }
            .decodeList()
        return rows.mapNotNull(::toGlobalRecord)
    }

    /** Recompose l'enregistrement global attendu par le noyau à partir d'une ligne. */
    private fun toGlobalRecord(row: JsonObject): String? {
        val offset = row["global_offset"]?.jsonPrimitive?.long ?: return null
        val envelope = row["envelope"]?.jsonPrimitive?.contentOrNull ?: return null
        return """{"global_offset":$offset,"envelope":$envelope}"""
    }
}
